import React from "react";
import { gaTrackCta } from "../../analytics/ga4";
import { responsiveGridColumns } from "../../core/breakpoints";
import { C } from "../../core/constants";
import { localizedPath } from "../../core/localePaths";
import { APP_FONT_SIZE } from "../../core/theme";
import { useI18n } from "../i18n/LocaleProvider";
import { useIsMobile } from "../../hooks/useMediaQuery";
import Button from "../../ui/Button";
import GlassCard from "../../ui/GlassCard";

export default function AboutPage({ acc, t: tProp }) {
  const { t: tContext, locale } = useI18n();
  const t = tProp || tContext;
  const fs = APP_FONT_SIZE;
  const isMobile = useIsMobile();
  const pillars = [
    {
      title: t("about_pillar_personalized_title"),
      text: t("about_pillar_personalized_text"),
    },
    {
      title: t("about_pillar_clarity_title"),
      text: t("about_pillar_clarity_text"),
    },
    {
      title: t("about_pillar_premium_title"),
      text: t("about_pillar_premium_text"),
    },
  ];

  const trustSignals = [
    t("about_signal_no_card"),
    t("about_signal_credits_never_expire"),
    t("about_signal_upgrade_when_needed"),
  ];

  return (
    <div style={{ position: "relative", zIndex: 1, paddingTop: 68 }}>
      <div style={{ maxWidth: 1060, margin: "0 auto", padding: "48px 28px 72px" }}>
        <GlassCard style={{ padding: "44px 40px", marginBottom: 24 }} glow accent={acc.primary}>
          <div style={{ fontSize: 12, letterSpacing: "0.14em", color: acc.primary, textTransform: "uppercase", marginBottom: 14 }}>{t("about_badge")}</div>
          <h1 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "clamp(36px,4vw,56px)", fontWeight: 400, color: C.textPrimary, lineHeight: 1.1, marginBottom: 14 }}>
            {t("about_title_line1")}
            <br />
            {t("about_title_line2")}
          </h1>
          <p style={{ fontSize: fs, color: C.textSecondary, lineHeight: 1.75, maxWidth: 760 }}>
            {t("about_intro")}
          </p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 28 }}>
            <Button
              variant="primary"
              acc={acc}
              onClick={() => {
                const signupDest = localizedPath("signUp", locale);
                gaTrackCta({ cta_id: "about_signup", cta_label: t("get_started_free"), destination: signupDest, screen_class: "about" });
                window.location.assign(signupDest);
              }}
              style={{ padding: "11px 24px", fontSize: 14 }}
            >
              {t("get_started_free")}
            </Button>
            <Button
              variant="outline"
              acc={acc}
              onClick={() => {
                const pricingDest = localizedPath("pricing", locale);
                gaTrackCta({ cta_id: "about_view_plans", cta_label: t("view_plans"), destination: pricingDest, screen_class: "about" });
                window.location.assign(pricingDest);
              }}
              style={{ padding: "11px 24px", fontSize: 14 }}
            >
              {t("view_plans")}
            </Button>
          </div>
        </GlassCard>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 16, marginBottom: 24 }}>
          {pillars.map((pillar) => (
            <GlassCard key={pillar.title} style={{ padding: "24px 20px" }} hoverable accent={acc.secondary} glow>
              <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 28, color: C.textPrimary, marginBottom: 10 }}>{pillar.title}</div>
              <p style={{ fontSize: 14, color: C.textSecondary, lineHeight: 1.7 }}>{pillar.text}</p>
            </GlassCard>
          ))}
        </div>

        <GlassCard style={{ padding: "28px 24px" }} accent={acc.tertiary}>
          <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 34, color: C.textPrimary, marginBottom: 14 }}>
            {t("about_why_convert")}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: responsiveGridColumns(isMobile, { desktop: "1fr 1fr", mobile: "1fr" }), gap: 16 }}>
            <div>
              <p style={{ fontSize: 14, color: C.textSecondary, lineHeight: 1.8 }}>
                {t("about_why_convert_desc")}
              </p>
            </div>
            <div>
              {trustSignals.map((signal) => (
                <div key={signal} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                  <span style={{ color: acc.primary }}>✦</span>
                  <span style={{ fontSize: 13, color: C.textSecondary }}>{signal}</span>
                </div>
              ))}
            </div>
          </div>
        </GlassCard>

        <GlassCard style={{ padding: "28px 24px", marginTop: 16 }} accent={acc.primary}>
          <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 34, color: C.textPrimary, marginBottom: 14 }}>
            {t("faq")}
          </div>
          <div style={{ display: "grid", gap: 12 }}>
            {[
              { q: t("faq_q_subscription_credits_linked"), a: t("faq_a_subscription_credits_linked") },
              { q: t("faq_q_credits_expire"), a: t("faq_a_credits_expire") },
              { q: t("faq_q_cancel_anytime"), a: t("faq_a_cancel_anytime") },
              { q: t("faq_q_reading_history"), a: t("faq_a_reading_history") },
            ].map((item) => (
              <div key={item.q} style={{ padding: "14px 16px", borderRadius: 12, border: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)" }}>
                <div style={{ fontSize: 14, color: C.textPrimary, marginBottom: 6 }}>{item.q}</div>
                <div style={{ fontSize: 13, color: C.textSecondary, lineHeight: 1.7 }}>{item.a}</div>
              </div>
            ))}
          </div>
        </GlassCard>

        <div style={{ textAlign: "center", marginTop: 28 }}>
          <Button
            variant="primary"
            acc={acc}
            onClick={() => {
              const pricingDest = localizedPath("pricing", locale);
              gaTrackCta({
                cta_id: "about_explore_pricing",
                cta_label: t("explore_credits_premium_plans"),
                destination: pricingDest,
                screen_class: "about",
              });
              window.location.assign(pricingDest);
            }}
            style={{ padding: "12px 32px", fontSize: 15 }}
          >
            {t("explore_credits_premium_plans")}
          </Button>
        </div>
      </div>
    </div>
  );
}
